import React, { useState, useEffect } from 'react';
import { X, Clock, Plus, Trash2, Save, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { doctorsApi, ScheduleEntry, DoctorScheduleResponse } from '../../lib/api';

interface ScheduleManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  doctorId: string;
  doctorName: string;
}

const daysOfWeek = [
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' },
] as const;

const ScheduleManagementModal: React.FC<ScheduleManagementModalProps> = ({
  isOpen,
  onClose,
  doctorId,
  doctorName
}) => {
  const [schedule, setSchedule] = useState<ScheduleEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && doctorId) {
      fetchSchedule();
    }
  }, [isOpen, doctorId]);

  const fetchSchedule = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await doctorsApi.getSchedule(doctorId);
      if (response.data.success && response.data.data) {
        setSchedule(response.data.data.schedule);
      }
    } catch (error: any) {
      console.error('Error fetching schedule:', error);
      setError('Failed to load schedule');
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const addScheduleEntry = () => {
    const newEntry: ScheduleEntry = {
      dayOfWeek: 'MONDAY',
      startTime: '09:00',
      endTime: '17:00',
      isAvailable: true
    };
    setSchedule([...schedule, newEntry]);
  };

  const updateScheduleEntry = (index: number, field: keyof ScheduleEntry, value: any) => {
    const updatedSchedule = [...schedule];
    updatedSchedule[index] = { ...updatedSchedule[index], [field]: value };
    setSchedule(updatedSchedule);
  };

  const removeScheduleEntry = (index: number) => {
    const updatedSchedule = schedule.filter((_, i) => i !== index);
    setSchedule(updatedSchedule);
  };

  const validateSchedule = () => {
    const errors: string[] = [];
    
    schedule.forEach((entry, index) => {
      if (entry.startTime >= entry.endTime) {
        errors.push(`Day ${index + 1}: Start time must be before end time`);
      }
    });

    // Check for overlapping times on the same day
    const dayGroups = schedule.reduce((acc, entry, index) => {
      if (!acc[entry.dayOfWeek]) {
        acc[entry.dayOfWeek] = [];
      }
      acc[entry.dayOfWeek].push({ ...entry, index });
      return acc;
    }, {} as Record<string, (ScheduleEntry & { index: number })[]>);

    Object.entries(dayGroups).forEach(([day, entries]) => {
      for (let i = 0; i < entries.length; i++) {
        for (let j = i + 1; j < entries.length; j++) {
          const entry1 = entries[i];
          const entry2 = entries[j];
          
          if (entry1.startTime < entry2.endTime && entry2.startTime < entry1.endTime) {
            errors.push(`${day}: Overlapping time slots found`);
            break;
          }
        }
      }
    });

    return errors;
  };

  const handleSave = async () => {
    try {
      const validationErrors = validateSchedule();
      if (validationErrors.length > 0) {
        toast.error(validationErrors[0]);
        return;
      }

      setSaving(true);
      setError(null);
      
      await doctorsApi.updateSchedule(doctorId, schedule);
      toast.success('Schedule updated successfully');
      onClose();
    } catch (error: any) {
      console.error('Error saving schedule:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save schedule';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const getDayLabel = (dayValue: string) => {
    return daysOfWeek.find(day => day.value === dayValue)?.label || dayValue;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Schedule Management</h2>
            <p className="text-sm text-gray-600 mt-1">Manage availability for {doctorName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading schedule...</p>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="h-4 w-4 mr-2" />
                  Set your weekly availability schedule
                </div>
                <button
                  onClick={addScheduleEntry}
                  className="btn-secondary"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Time Slot
                </button>
              </div>

              {schedule.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No schedule entries yet. Add your first time slot to get started.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {schedule.map((entry, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
                        {/* Day of Week */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Day
                          </label>
                          <select
                            value={entry.dayOfWeek}
                            onChange={(e) => updateScheduleEntry(index, 'dayOfWeek', e.target.value)}
                            className="input text-sm"
                          >
                            {daysOfWeek.map(day => (
                              <option key={day.value} value={day.value}>
                                {day.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Start Time */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Start Time
                          </label>
                          <input
                            type="time"
                            value={entry.startTime}
                            onChange={(e) => updateScheduleEntry(index, 'startTime', e.target.value)}
                            className="input text-sm"
                          />
                        </div>

                        {/* End Time */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            End Time
                          </label>
                          <input
                            type="time"
                            value={entry.endTime}
                            onChange={(e) => updateScheduleEntry(index, 'endTime', e.target.value)}
                            className="input text-sm"
                          />
                        </div>

                        {/* Available Toggle */}
                        <div className="flex items-center">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={entry.isAvailable}
                              onChange={(e) => updateScheduleEntry(index, 'isAvailable', e.target.checked)}
                              className="sr-only"
                            />
                            <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              entry.isAvailable ? 'bg-blue-600' : 'bg-gray-200'
                            }`}>
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  entry.isAvailable ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </div>
                            <span className="ml-2 text-sm text-gray-700">
                              {entry.isAvailable ? 'Available' : 'Unavailable'}
                            </span>
                          </label>
                        </div>

                        {/* Remove Button */}
                        <div className="flex justify-end">
                          <button
                            onClick={() => removeScheduleEntry(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={saving}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="btn-primary"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Schedule
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleManagementModal; 